// Pipeline stages
export const PIPELINE_STAGES = [
  'sourced',
  'applied',
  'recruiter_screen',
  'shortlist',
  'sent_to_client',
  'interview',
  'offer',
  'hired',
  'disqualified',
] as const;

export type PipelineStage = typeof PIPELINE_STAGES[number];

export const STAGE_LABELS: Record<PipelineStage, string> = {
  sourced: 'Sourced',
  applied: 'Applied',
  recruiter_screen: 'Recruiter Screen',
  shortlist: 'Shortlist',
  sent_to_client: 'Sent to Client',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  disqualified: 'Disqualified',
};

export const STAGE_COLORS: Record<PipelineStage, string> = {
  sourced: 'bg-gray-500',
  applied: 'bg-blue-500',
  recruiter_screen: 'bg-cyan-500',
  shortlist: 'bg-emerald-500',
  sent_to_client: 'bg-yellow-500',
  interview: 'bg-orange-500',
  offer: 'bg-pink-500',
  hired: 'bg-green-500',
  disqualified: 'bg-red-500',
};

// User roles
export const USER_ROLES = ['admin', 'account_manager', 'recruiter', 'viewer'] as const;
export type UserRole = typeof USER_ROLES[number];

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  account_manager: 'Account Manager',
  recruiter: 'Recruiter',
  viewer: 'Viewer',
};

// Job statuses
export const JOB_STATUSES = ['draft', 'open', 'paused', 'filled', 'cancelled'] as const;
export type JobStatus = typeof JOB_STATUSES[number];

export const STATUS_LABELS: Record<JobStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  paused: 'Paused',
  filled: 'Filled',
  cancelled: 'Cancelled',
};

// Inverted label style: colored text + border on a dark tinted background.
export const STATUS_COLORS: Record<JobStatus, string> = {
  draft: 'border border-white/20 bg-white/10 text-text-secondary',
  open: 'border border-green-500/30 bg-green-500/15 text-green-400',
  paused: 'border border-amber-500/30 bg-amber-500/15 text-amber-400',
  filled: 'border border-blue-500/30 bg-blue-500/15 text-blue-400',
  cancelled: 'border border-red-500/30 bg-red-500/15 text-red-400',
};

// Job types
export const JOB_TYPES = ['full_time', 'part_time', 'contract', 'internship'] as const;
export type JobType = typeof JOB_TYPES[number];

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  contract: 'Contract',
  internship: 'Internship',
};

// Candidate interface
export interface Candidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  whatsappPhone: string;
  phoneSecondary: string | null;
  currentLocation: string | null;
  nationality: string | null;
  yearsExperience: number | null;
  currentJobTitle: string | null;
  currentEmployer: string | null;
  skills: string[];
  preferredJobTypes: string[];
  expectedSalaryRange: string | null;
  availabilityDate: string | null;
  linkedinUrl: string | null;
  source: string;
  onboardingStatus: string;
  tags?: Array<{ id: string; name: string; color: string }>;
  createdAt: string;
  updatedAt: string;
  // Data quality (computed by backend, optional for backward compat)
  data_quality_score?: number;
  missing_fields?: string[];
}

// Job interface
export interface Job {
  id: string;
  title: string;
  location: string | null;
  jobType: JobType | null;
  remoteType: string | null;
  description: string | null;
  requirements: string | null;
  candidateSafeTitle: string | null;
  candidateSafeDescription: string | null;
  requiredSkills: string[];
  experienceMin: number | null;
  experienceMax: number | null;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string;
  salaryFrequency: string;
  status: JobStatus;
  priority: string;
  positionsAvailable: number;
  deadline: string | null;
  createdAt: string;
  updatedAt: string;
  company?: {
    id: string;
    name: string;
  } | null;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  applicationCount?: number;
}

// Application interface
export interface Application {
  id: string;
  jobId: string;
  candidateId: string;
  currentStage: PipelineStage;
  disqualificationReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  job?: Job;
  candidate?: Candidate;
}

// Tag interface
export interface Tag {
  id: string;
  name: string;
  color: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

// Candidate Tag assignment
export interface CandidateTag {
  candidateId: string;
  tagId: string;
  tag: Tag;
  assignedAt: string;
  assignedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

// Tag colors available for selection
export const TAG_COLORS = [
  { name: 'Gray', value: '#6B7280' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Lime', value: '#84CC16' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Sky', value: '#0EA5E9' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Purple', value: '#A855F7' },
  { name: 'Fuchsia', value: '#D946EF' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Rose', value: '#F43F5E' },
] as const;

// Pagination
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Notification
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Notification {
  id: string;
  type: string;
  category: string;
  priority: NotificationPriority;
  title: string;
  message: string;
  entityType: string | null;
  entityId: string | null;
  dedupeKey?: string | null;
  isRead: boolean;
  readAt: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface NotificationPreferences {
  inAppMentions: boolean;
  inAppPipeline: boolean;
  inAppInterviews: boolean;
  inAppSubmissions: boolean;
  inAppInvoices: boolean;
  inAppPlacements: boolean;
  inAppJobs: boolean;
  inAppCandidates: boolean;
  inAppCompanies: boolean;
  inAppSystem: boolean;
  minPriority: NotificationPriority;
  emailDigest: boolean;
}

// Email
export interface Email {
  id: string;
  toAddress: string;
  fromAddress: string;
  subject: string;
  body: string;
  status: string;
  candidateId: string | null;
  jobId: string | null;
  sentBy: { id: string; firstName: string; lastName: string } | null;
  sentAt: string;
  createdAt: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  variables: string[];
  category: string | null;
  isDefault: boolean;
  createdAt: string;
}

// Submission
export interface SubmissionFeedback {
  id: string;
  rating: number | null;
  decision: string | null;
  comments: string | null;
  createdAt: string;
}

export interface CandidateSubmission {
  id: string;
  companyId: string;
  jobId: string;
  candidateId: string;
  status: string;
  coverNote: string | null;
  candidate: { id: string; firstName: string; lastName: string; currentJobTitle: string | null };
  job: { id: string; title: string };
  company: { id: string; name: string };
  submittedBy: { id: string; firstName: string; lastName: string } | null;
  feedback?: SubmissionFeedback[];
  createdAt: string;
}

// Placement
export interface Placement {
  id: string;
  candidateId: string;
  jobId: string;
  companyId: string;
  startDate: string | null;
  endDate: string | null;
  salary: number | null;
  salaryCurrency: string;
  feeType: string;
  feePercentage: number | null;
  feeAmount: number | null;
  status: string;
  notes: string | null;
  candidate: { id: string; firstName: string; lastName: string };
  job: { id: string; title: string };
  company: { id: string; name: string };
  commissions: Commission[];
  createdAt: string;
}

export interface Commission {
  id: string;
  userId: string;
  splitPercentage: number;
  amount: number;
  status: string;
  user: { id: string; firstName: string; lastName: string };
}

// Invoice
export interface Invoice {
  id: string;
  invoiceNumber: string;
  companyId: string;
  placementId: string | null;
  issueDate: string;
  dueDate: string | null;
  currency: string;
  taxRate: number;
  taxExempt: boolean;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  status: string;
  voidReason: string | null;
  notes: string | null;
  pdfStorageKey: string | null;
  isCreditNote: boolean;
  parentInvoiceId: string | null;
  recurringConfig: RecurringConfig | null;
  company: { id: string; name: string };
  placement?: {
    id: string;
    candidate: { id: string; firstName: string; lastName: string };
    job: { id: string; title: string };
  } | null;
  lineItems: InvoiceLineItem[];
  lineItemCount: number;
  payments?: InvoicePayment[];
  creditNotes?: { id: string; invoiceNumber: string; totalAmount: number; status: string; createdAt: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoicePayment {
  id: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  reference: string | null;
  notes: string | null;
  createdBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface RecurringConfig {
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'annually';
  nextDate: string;
  endDate?: string | null;
  autoSend: boolean;
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  sortOrder: number;
  createdAt: string;
}

// Interview
export interface Interview {
  id: string;
  candidateId: string;
  jobId: string;
  applicationId: string | null;
  title: string;
  type: string;
  scheduledAt: string;
  durationMinutes: number;
  location: string | null;
  meetingLink: string | null;
  status: string;
  feedback: string | null;
  rating: number | null;
  notes: string | null;
  candidate: { id: string; firstName: string; lastName: string };
  job: { id: string; title: string };
  attendees: InterviewAttendee[];
  createdAt: string;
}

export interface InterviewAttendee {
  id: string;
  userId: string | null;
  externalName: string | null;
  externalEmail: string | null;
  role: string;
  rsvpStatus: string;
  user?: { id: string; firstName: string; lastName: string };
}

// Job Board
export interface JobBoardAccount {
  id: string;
  provider: string;
  isActive: boolean;
  createdAt: string;
}

export interface JobBoardPosting {
  id: string;
  jobId: string;
  accountId: string;
  externalId: string | null;
  externalUrl: string | null;
  status: string;
  postedAt: string | null;
  views: number;
  clicks: number;
  applicationsCount: number;
  job: { id: string; title: string };
  account: { id: string; provider: string };
  createdAt: string;
}

// Report types
export interface PipelineFunnelData {
  stage: string;
  count: number;
  percentage: number;
}

export interface RecruiterPerformance {
  userId: string;
  firstName: string;
  lastName: string;
  applications: number;
  hired: number;
  interviews: number;
}

export interface TimeToHireData {
  averageDays: number;
  byMonth: Array<{ month: string; averageDays: number; count: number }>;
}

export interface SourceAnalysisData {
  source: string;
  count: number;
  percentage: number;
}
